import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileText } from 'lucide-react';
import { useAppContext } from '@/contexts/AppContext';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { formatDate } from '@/lib/dates';

interface DriverSummaryProps {
  driverName: string;
  startDate: string;
  endDate: string;
}

interface JobData {
  date: string;
  jobId: string;
  amount: number;
}

const DriverSummarySimple: React.FC<DriverSummaryProps> = ({ driverName, startDate, endDate }) => {
  const { drivers } = useAppContext();
  const [jobs, setJobs] = useState<JobData[]>([]);
  const [loading, setLoading] = useState(true);

  const currentDriver = drivers.find(d => d.name === driverName);

  useEffect(() => {
    loadDriverData();
  }, [driverName, startDate, endDate]);

  const loadDriverData = async () => {
    if (!currentDriver) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('driver_completed_payments')
        .select('pu_date, taxicaller_job_id, paid_amount')
        .eq('driver_name', driverName)
        .gte('pu_date', startDate)
        .lte('pu_date', endDate)
        .or('cash_amt.gt.0, eftpos_amt.gt.0, totalmobility_amt.gt.0, account_amt.gt.0, ecard_amt.gt.0')
        .eq('total_mobility_valid', true)
        .eq('eftpos_valid', true)
        .eq('ecard_valid', true)
        .eq('tm_hoist_valid', true);

      if (error) {
        console.error('Error loading data:', error);
        toast({
          title: 'Error loading data',
          description: error.message,
          variant: 'destructive',
        });
        return;
      }

      if (data) {
        const jobData: JobData[] = data.map(job => ({
          date: job.pu_date,
          jobId: job.taxicaller_job_id,
          amount: parseFloat(job.paid_amount || 0),
        }));
        setJobs(jobData);
      }
    } catch (error) {
      console.error('Error loading driver data:', error);
      toast({
        title: 'Error loading data',
        description: 'Failed to load driver data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const totalEarnings = jobs.reduce((sum, job) => sum + job.amount, 0);

  const generateExcel = () => {
    const csvContent = [
      ['Driver Payment Summary'],
      [`Driver: ${driverName}`],
      [`Period: ${formatDate(startDate)} - ${formatDate(endDate)}`],
      [''],
      ['Date', 'Job ID', 'Amount'],
      ...jobs.map(job => [
        formatDate(job.date),
        job.jobId,
        `$${job.amount.toFixed(2)}`
      ]),
      [''],
      ['Total Earnings', `$${totalEarnings.toFixed(2)}`]
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `driver-summary-${driverName.replace(/\s+/g, '-')}-${Date.now()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: 'Excel Generated',
      description: 'Driver summary downloaded.',
    });
  };

  if (loading) {
    return (
      <Card className="shadow-lg">
        <CardContent className="p-6">
          <div className="text-center">Loading driver data...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-lg">
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="text-xl text-blue-800">
              Driver Summary - {driverName}
            </CardTitle>
            <p className="text-sm text-gray-600">
              Period: {formatDate(startDate)} - {formatDate(endDate)}
            </p>
          </div>
          <Button onClick={generateExcel} className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Download Excel
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {jobs.length > 0 ? (
          <div className="space-y-4">
            <div className="text-lg font-semibold text-green-600">
              Total Earnings: ${totalEarnings.toFixed(2)}
            </div>
            <div className="space-y-3">
              {jobs.map((job, index) => (
                <div key={index} className="border rounded-lg p-3 space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">{formatDate(job.date)}</span>
                    <span className="font-semibold text-green-600">${job.amount.toFixed(2)}</span>
                  </div>
                  <div className="text-sm text-gray-500">Job ID: {job.jobId}</div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No validated job data found for this period
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DriverSummarySimple;
